import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { AssistantUiChatMessage, ChatMessage } from './chat-message';
import type { ChatErrorCode, ChatErrorResponse, ChatHistory, ChatRefreshRequest, ChatRefreshResponse, ChatRequest, ChatResponse, ChatUiItem } from './contract/chat-dto';
import { parseUITree } from './contract/uitree.types';
import { CustomerChatConfig } from './customer-chat-config';
import { CustomerChatTokenProvider } from './customer-chat-token-provider';
import { updatePeriodNavInTree } from './shift-period';

// v2: the BFF moved to server-side conversation state, so `history` now holds an opaque response id
// rather than a model transcript. A restored v1 session would show a conversation the model has no
// memory of, so drop transcript and state together.
const STORAGE_KEY = 'nscp-customer-chat-session-v2';

interface StoredSession {
  messages: ChatMessage[];
  history: ChatHistory;
}

/** A client-safe error surfaced as a signal; the UI maps `code` to a Transloco string (M5). */
export interface ChatError {
  code: ChatErrorCode;
  message: string;
}

/**
 * Fallback shown when a `ui` item fails client-side validation. The UI layer (M5) may replace this
 * with a translated string; kept here so the transcript never silently drops content.
 */
export const UI_FALLBACK_TEXT = 'Deze weergave kon niet worden getoond.';

/**
 * Drives the chat against the BFF (`POST {baseUrl}/api/chat`).
 *
 * The BFF is a different origin than the portal API, so the shared `apiAuthorizationToken`
 * interceptor does not attach the bearer token — this client sets `Authorization` explicitly from
 * {@link CustomerChatTokenProvider}. State is exposed as signals; `history` is the opaque server state
 * echoed back on each turn.
 */
@Injectable({ providedIn: 'root' })
export class CustomerChatClient {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CustomerChatConfig);
  private readonly tokenProvider = inject(CustomerChatTokenProvider);

  private readonly messagesState = signal<ChatMessage[]>([]);
  private readonly loadingState = signal(false);
  private readonly refreshingState = signal<string | null>(null);
  private readonly errorState = signal<ChatError | null>(null);
  private history: ChatHistory = [];
  /** Monotonic source for `assistant-ui` message ids — no Date.now()/random, so it is SSR/test-safe. */
  private nextUiId = 0;

  constructor() {
    // Restore previous session unless the user explicitly reloaded the page (F5 / hard refresh).
    // PerformanceNavigationTiming is a document-level entry; its type is 'reload' on F5 and
    // 'navigate' on initial loads and SPA navigations. Guard for environments (SSR, tests) where
    // the Performance API may be absent.
    const nav =
      typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
        ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
        : undefined;
    if (nav?.type !== 'reload') {
      this.restoreSession();
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /** The transcript, in display order. */
  public readonly messages = this.messagesState.asReadonly();
  /** True while a normal turn (send / append action) is in flight. */
  public readonly loading = this.loadingState.asReadonly();
  /** Id of the `assistant-ui` message currently refreshing in place (M7.5), or null. */
  public readonly refreshingId = this.refreshingState.asReadonly();
  /** The last error, or null. Cleared at the start of each send. */
  public readonly error = this.errorState.asReadonly();

  /** Sends a user message and appends the assistant's reply. Never throws — errors land in `error()`. */
  public async sendMessage(text: string): Promise<void> {
    const message = text.trim();
    if (!message || this.loadingState()) {
      return;
    }

    const token = this.tokenProvider.token();
    if (!token) {
      // Surface a clear, recoverable state instead of throwing (mirrors the BFF 401 contract).
      this.errorState.set({ code: 'mcp_unauthorized', message: 'Not authenticated.' });
      return;
    }

    this.append({ kind: 'user', text: message });
    this.loadingState.set(true);
    this.errorState.set(null);

    const body: ChatRequest = { message, history: this.history };

    try {
      const response = await firstValueFrom(
        this.http.post<ChatResponse>(`${this.config.baseUrl}/api/chat`, body, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
      this.history = response.history ?? [];
      this.appendResponse(response);
      this.saveSession();
    } catch (err) {
      this.errorState.set(this.mapError(err));
    } finally {
      this.loadingState.set(false);
    }
  }

  /**
   * In-place refresh (M7.5): updates the existing `assistant-ui` view with `id === sourceId` — no user
   * bubble, no new card; only that card shows a scoped loading state via {@link refreshingId}. Used
   * for `replace`-mode actions (periodNav/toggle).
   *
   * Two paths:
   *  - **Phase 2 fast path** — if the view is **data-bound** (carries a `data` blob from a tool), the
   *    `params` (e.g. the client-shifted period) are merged into its origin `input` and a single tool
   *    is re-run via `/api/refresh`: **no LLM call, no history mutation**, just a fresh `data`
   *    blob the renderer re-binds against (the tree shape is identical → it reads as navigation).
   *  - **Phase 1 fallback** — otherwise `message` runs as a hidden `replace:true` turn through the
   *    agentic loop and the rendered view is swapped in place; the synthetic turn advances `history`
   *    so the model stays coherent.
   *
   * Never throws — failures land in `error()` and leave the current view intact.
   */
  public async replaceMessage(sourceId: string, message: string, params: Record<string, unknown> = {}): Promise<void> {
    // Ignore if a refresh/send is already in flight.
    if (this.refreshingState() !== null || this.loadingState()) {
      return;
    }

    const target = this.messagesState().find((m): m is AssistantUiChatMessage => m.kind === 'assistant-ui' && m.id === sourceId);
    // Fast path: a data-bound view refreshes deterministically — no LLM, no history.
    if (target?.data && target.tool) {
      await this.refreshView(target, params);
      return;
    }

    const text = message.trim();
    if (!text) {
      return;
    }

    const token = this.tokenProvider.token();
    if (!token) {
      this.errorState.set({ code: 'mcp_unauthorized', message: 'Not authenticated.' });
      return;
    }

    this.refreshingState.set(sourceId);
    this.errorState.set(null);

    const body: ChatRequest = { message: text, history: this.history, replace: true };

    try {
      const response = await firstValueFrom(
        this.http.post<ChatResponse>(`${this.config.baseUrl}/api/chat`, body, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
      this.history = response.history ?? [];
      this.applyReplacement(sourceId, response);
      this.saveSession();
    } catch (err) {
      this.errorState.set(this.mapError(err));
    } finally {
      this.refreshingState.set(null);
    }
  }

  /**
   * Phase 2 deterministic refresh: re-run the view's origin tool with `params` merged into its
   * `input` via `POST /api/refresh` (no LLM, no history), then swap the `data` blob, `input`,
   * and the UiTree's `periodNav` nodes (prevParams/nextParams/label) so navigation chains correctly.
   */
  private async refreshView(target: AssistantUiChatMessage, params: Record<string, unknown>): Promise<void> {
    const token = this.tokenProvider.token();
    if (!token) {
      this.errorState.set({ code: 'mcp_unauthorized', message: 'Not authenticated.' });
      return;
    }

    this.refreshingState.set(target.id);
    this.errorState.set(null);

    const body: ChatRefreshRequest = { tool: target.tool, input: { ...target.input, ...params } };

    try {
      const response = await firstValueFrom(
        this.http.post<ChatRefreshResponse>(`${this.config.baseUrl}/api/refresh`, body, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
      const newInput = response.input ?? body.input;
      const updatedTree = updatePeriodNavInTree(target.uiTree, newInput);
      this.messagesState.update((messages) =>
        messages.map((m) => (m.kind === 'assistant-ui' && m.id === target.id ? { ...m, input: newInput, data: response.data, uiTree: updatedTree } : m)),
      );
      this.saveSession();
    } catch (err) {
      this.errorState.set(this.mapError(err));
    } finally {
      this.refreshingState.set(null);
    }
  }

  /** Clears the transcript and conversation state (e.g. a "new chat" action). */
  public reset(): void {
    this.messagesState.set([]);
    this.errorState.set(null);
    this.history = [];
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private restoreSession(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const { messages, history } = JSON.parse(raw) as StoredSession;
      if (!Array.isArray(messages) || !Array.isArray(history)) return;
      // Re-validate UITree nodes so a schema change never renders corrupt data; preserve the M7.5
      // `id`/`input` fields (defaulting `input` for sessions written before they existed).
      const validated = messages.map((m) => {
        if (m.kind === 'assistant-ui') {
          const tree = parseUITree(m.uiTree);
          return tree ? ({ ...m, input: m.input ?? {}, uiTree: tree } satisfies ChatMessage) : ({ kind: 'assistant-text', text: UI_FALLBACK_TEXT } satisfies ChatMessage);
        }
        return m;
      });
      this.seedUiIdCounter(validated);
      this.messagesState.set(validated);
      this.history = history;
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  private saveSession(): void {
    try {
      const payload: StoredSession = { messages: this.messagesState(), history: this.history };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // sessionStorage unavailable or quota exceeded — session still works in memory
    }
  }

  private appendResponse(response: ChatResponse): void {
    for (const item of response.items) {
      if (item.kind === 'text') {
        this.append({ kind: 'assistant-text', text: item.text });
        continue;
      }
      // Defensive re-validation: the BFF already validated, but never trust the wire.
      const tree = parseUITree(item.uiTree);
      if (tree) {
        // Carry `id` + `input` (+ `data` for bound trees) so an in-place refresh (M7.5) can target this
        // view, reconstruct the call, and re-resolve the same tree against fresh data.
        this.append({ kind: 'assistant-ui', id: this.newUiId(), tool: item.tool, input: item.input ?? {}, uiTree: tree, data: item.data });
      } else {
        this.append({ kind: 'assistant-text', text: UI_FALLBACK_TEXT });
      }
    }
  }

  /** Swap the validated UITree (and origin) of the `assistant-ui` message with `id === sourceId`. */
  private applyReplacement(sourceId: string, response: ChatResponse): void {
    const uiItem = response.items.find((it): it is ChatUiItem => it.kind === 'ui');
    const tree = uiItem ? parseUITree(uiItem.uiTree) : null;
    if (!uiItem || !tree) {
      // The refresh produced no valid view — keep the current one and surface a recoverable error.
      this.errorState.set({ code: 'internal', message: 'The view could not be updated.' });
      return;
    }
    this.messagesState.update((messages) =>
      messages.map((m) => (m.kind === 'assistant-ui' && m.id === sourceId ? { ...m, tool: uiItem.tool, input: uiItem.input ?? {}, uiTree: tree, data: uiItem.data } : m)),
    );
  }

  private newUiId(): string {
    return `ui-${this.nextUiId++}`;
  }

  private append(message: ChatMessage): void {
    this.messagesState.update((messages) => [...messages, message]);
  }

  /** Advance the id counter past any restored `ui-<n>` ids so freshly appended views stay unique. */
  private seedUiIdCounter(messages: ChatMessage[]): void {
    let max = -1;
    for (const m of messages) {
      if (m.kind === 'assistant-ui') {
        const match = /^ui-(\d+)$/.exec(m.id ?? '');
        if (match) max = Math.max(max, Number(match[1]));
      }
    }
    this.nextUiId = max + 1;
  }

  private mapError(err: unknown): ChatError {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as Partial<ChatErrorResponse> | null;
      if (body?.error?.code) {
        return { code: body.error.code, message: body.error.message };
      }
      if (err.status === 401 || err.status === 403) {
        return { code: 'mcp_unauthorized', message: 'Unauthorized.' };
      }
      if (err.status === 0) {
        return { code: 'internal', message: 'Cannot reach the chat service.' };
      }
    }
    return { code: 'internal', message: 'Unexpected error.' };
  }
}
